import React from 'react';
import { YStack } from 'tamagui';
import loadImage from 'blueimp-load-image';
import CobuText from './CobuText';
import CobuButton from './CobuButton';
import CobuImage from './CobuImage';
import CobuStack from '../layout/CobuStack';

// Normalize image orientation based on EXIF (handles iOS rotation issues)
async function fixImageOrientation(file: File): Promise<File> {
    return new Promise((resolve) => {
        loadImage(
            file,
            (eventOrImage) => {
                // Error case
                if (eventOrImage instanceof Event) {
                    console.error('[CobuImagePicker] Error loading image:', eventOrImage);
                    resolve(file);
                    return;
                }

                // Success case: canvas with corrected orientation
                if (eventOrImage instanceof HTMLCanvasElement) {
                    eventOrImage.toBlob((blob) => {
                        if (blob) {
                            const correctedFile = new File([blob], file.name, {
                                type: file.type,
                                lastModified: file.lastModified,
                            });
                            resolve(correctedFile);
                        } else {
                            console.error('[CobuImagePicker] Failed to create blob');
                            resolve(file);
                        }
                    }, file.type);
                } else {
                    // Unexpected type (HTMLImageElement) – fall back to original file
                    console.error('[CobuImagePicker] Expected canvas but got image element');
                    resolve(file);
                }
            },
            {
                orientation: true, // Automatically apply EXIF orientation
                canvas: true,      // Return a canvas element
                maxWidth: 4096,
                maxHeight: 4096,
            }
        );
    });
}

type CobuImagePickerProps = {
    label?: string;
    value: File | null;
    onChange: (file: File | null) => void;
    onRemove?: () => void;
    error?: string;
    placeholder?: string;
    removeLabel?: string;
    heightToken?: string; // e.g. "$56"
    initialUri?: string; // optional initial image URL for prefill (edit mode)
};

const CobuImagePicker: React.FC<CobuImagePickerProps> = ({
    label,
    value,
    onChange,
    onRemove,
    error,
    placeholder,
    removeLabel,
    heightToken = '$56',
    initialUri,
}) => {
    const fileInputRef = React.useRef<HTMLInputElement | null>(null);
    const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
    const [ignoreInitial, setIgnoreInitial] = React.useState(false);

    React.useEffect(() => {
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl]);

    // Initialize preview from an external URL (e.g., existing image on edit)
    React.useEffect(() => {
        if (!ignoreInitial && !previewUrl && initialUri) {
            setPreviewUrl(initialUri);
        }
    }, [initialUri, previewUrl, ignoreInitial]);

    const handleOpenPicker = () => {
        setIgnoreInitial(true);
        fileInputRef.current?.click();
    };

    const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        if (!file) {
            onChange(null);
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
            return;
        }

        // Always show the original file in the preview (browser will honor EXIF)
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(URL.createObjectURL(file));

        try {
            // Normalize orientation for the file we actually upload
            const correctedFile = await fixImageOrientation(file);
            onChange(correctedFile);
        } catch (error) {
            console.error('[CobuImagePicker] Error fixing image orientation:', error);
            // Fallback to original file if anything goes wrong
            onChange(file);
        }
    };

    const handleRemove = () => {
        if (previewUrl && previewUrl.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
        setIgnoreInitial(true);
        onChange(null);
        onRemove?.();
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    return (
        <YStack gap="$1">
            {label ? (
                <CobuText variant="label" color="$color">
                    {label}
                </CobuText>
            ) : null}

            <CobuStack
                h={heightToken as any}
                br="$3"
                ov="hidden"
                ai="center"
                jc="center"
                boc={error ? ('$error' as any) : ('$borderColor' as any)}
                bw={'$0.5' as any}
                bg={previewUrl ? (undefined as any) : ('$cobuDarkBlue10' as any)}
                onPress={handleOpenPicker}
            >
                {previewUrl ? (
                    <CobuImage source={{ uri: previewUrl }} width="100%" height="100%" />
                ) : (
                    <YStack ai="center" gap="$2" px="$3">
                        <YStack ai="center" jc="center">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ color: '#808080' }}>
                                <circle cx="12" cy="12" r="3.2" />
                                <path d="M9 2 7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5" />
                            </svg>
                        </YStack>
                        <CobuText variant="body" color="$colorMuted" ta="center">
                            {placeholder}
                        </CobuText>
                    </YStack>
                )}
            </CobuStack>
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleChange}
            />

            {previewUrl ? (
                <CobuButton dsVariant="ghost" title={removeLabel || 'Remove photo'} onPress={handleRemove} />
            ) : null}

            {error ? (
                <CobuText variant="caption" color="$error">
                    {error}
                </CobuText>
            ) : null}
        </YStack>
    );
};

export default CobuImagePicker;


